import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Table,
  Card,
  Icon,
  Segment,
  Message,
} from "semantic-ui-react";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import URL from "../config/config.json";

const baseUrl = URL.baseUrl;

function OfferLetterVerifyModal({
  isOfferLetterVerifyModalOpen,
  setIsOfferLetterVerifyModalOpen,
}) {
  const toast = useToast();
  
  // State for Offer Letter Verification
  const [offerLetterId, setOfferLetterId] = useState("");
  const [offerLetterData, setOfferLetterData] = useState(null);
  const [offerLetterLoading, setOfferLetterLoading] = useState(false);
  const [offerLetterError, setOfferLetterError] = useState(null);
  const [hintToastShown, setHintToastShown] = useState(false);

  useEffect(() => {
    if (isOfferLetterVerifyModalOpen) {
      // Reset all data on modal open
      resetAllData();
    }
  }, [isOfferLetterVerifyModalOpen]);

  const resetAllData = () => {
    setOfferLetterData(null);
    setOfferLetterError(null);
    setOfferLetterId("");
    setHintToastShown(false);
  };

  const handleOfferLetterVerify = async () => {
    if (!offerLetterId.trim()) {
      setOfferLetterError("Please enter an Offer Letter ID");
      return;
    }
    
    setOfferLetterLoading(true);
    setOfferLetterError(null);
    
    try {
      const response = await axios.get(`${baseUrl}/get_offer_letter_by_id/${offerLetterId}/`);
      setOfferLetterData(response.data);
    } catch (error) {
      setOfferLetterError("Offer letter not found or invalid ID. Please check and try again.");
      setOfferLetterData(null);
    } finally {
      setOfferLetterLoading(false);
    }
  };

  const showHintToast = () => {
    if (!hintToastShown) {
      toast({
        title: "Sample Offer Letter ID: OL2025090206917_3D9888BC7",
        status: "info",
        position: "top",
        isClosable: true,
        duration: 4000,
      });
      setHintToastShown(true);
    }
  };

  const formatSalary = (salary) => {
    if (!salary) return 'Not specified';
    return `₹${parseInt(salary.replace(/,/g, '')).toLocaleString('en-IN')} per annum`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Function to check if acceptance deadline has expired
  const isAcceptanceDeadlineExpired = (acceptanceDeadline) => {
    if (!acceptanceDeadline) return false;
    try {
      const deadlineDate = new Date(acceptanceDeadline);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      deadlineDate.setHours(23, 59, 59, 999);
      return currentDate > deadlineDate;
    } catch (error) {
      return false;
    }
  };

  // Function to calculate days difference
  const getDaysDifference = (acceptanceDeadline) => {
    if (!acceptanceDeadline) return 0;
    try {
      const deadlineDate = new Date(acceptanceDeadline);
      const currentDate = new Date();
      const timeDiff = deadlineDate.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return daysDiff;
    } catch (error) {
      return 0;
    }
  };

  const isExpired = offerLetterData && isAcceptanceDeadlineExpired(offerLetterData.acceptance_deadline);
  const daysDiff = offerLetterData ? getDaysDifference(offerLetterData.acceptance_deadline) : 0;

  return (
    <Modal
      closeIcon
      open={isOfferLetterVerifyModalOpen}
      onClose={() => setIsOfferLetterVerifyModalOpen(false)}
      size="large"
      style={{ height: 'auto' }}
    >
      <Modal.Header style={{ 
        backgroundColor: '#f8f9fa', 
        borderBottom: '2px solid #e9ecef',
        padding: '20px 30px'
      }}>
        <Icon name="file alternate" color="green" />
        Offer Letter Verification Portal
      </Modal.Header>

      <Modal.Content style={{ padding: '30px' }}>
        <Segment style={{ minHeight: '500px', border: 'none', boxShadow: 'none' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <Icon name="file alternate" size="huge" color="green" />
            <h2 style={{ color: '#21ba45', marginTop: '10px' }}>Offer Letter Verification</h2>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Enter your offer letter ID to verify its authenticity
            </p>
          </div>

          <Card fluid style={{ padding: '20px' }}>
            <Form>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Offer Letter ID *
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Input
                    type="text"
                    placeholder="Enter your Offer Letter ID"
                    value={offerLetterId}
                    onChange={(e) => setOfferLetterId(e.target.value)}
                    style={{ flex: 1 }}
                    size="large"
                    onKeyPress={(e) => e.key === 'Enter' && handleOfferLetterVerify()}
                  />
                  <span 
                    onClick={showHintToast} 
                    style={{ 
                      cursor: "pointer",
                      color: "#21ba45",
                      fontSize: '14px',
                      textDecoration: 'underline'
                    }}
                  >
                    Need help? View sample ID
                  </span>
                </div>
              </div>

              <Button
                color="green"
                onClick={handleOfferLetterVerify}
                loading={offerLetterLoading}
                disabled={offerLetterLoading || !offerLetterId.trim()}
                size="large"
                style={{ 
                  width: '200px',
                  display: 'block',
                  margin: '20px auto'
                }}
              >
                <Icon name="search" />
                Verify Offer Letter
              </Button>
            </Form>

            {offerLetterError && (
              <Message negative style={{ marginTop: '20px' }}>
                <Icon name="exclamation triangle" />
                {offerLetterError}
              </Message>
            )}

            {offerLetterData && (
              <div style={{ marginTop: '30px' }}>
                <Message positive>
                  <Icon name="check circle" />
                  Offer letter verified successfully!
                </Message>

                {/* Expiration Warning Message */}
                {isExpired && (
                  <Message negative style={{ marginTop: '15px' }}>
                    <Message.Header>
                      <Icon name="exclamation triangle" />
                      Acceptance Deadline Expired
                    </Message.Header>
                    <p>
                      The acceptance deadline for this offer letter has expired on{' '}
                      <strong>{formatDate(offerLetterData.acceptance_deadline)}</strong>.
                      {Math.abs(daysDiff) === 1 
                        ? ` It expired ${Math.abs(daysDiff)} day ago.`
                        : ` It expired ${Math.abs(daysDiff)} days ago.`
                      }
                    </p>
                  </Message>
                )}

                {/* Deadline Warning for Soon-to-Expire (within 3 days) */}
                {!isExpired && daysDiff <= 3 && daysDiff > 0 && (
                  <Message warning style={{ marginTop: '15px' }}>
                    <Message.Header>
                      <Icon name="clock outline" />
                      Urgent: Acceptance Deadline Approaching
                    </Message.Header>
                    <p>
                      The acceptance deadline is approaching! You have{' '}
                      <strong>{daysDiff} {daysDiff === 1 ? 'day' : 'days'}</strong> remaining to accept this offer.
                      Deadline: <strong>{formatDate(offerLetterData.acceptance_deadline)}</strong>
                    </p>
                  </Message>
                )}
                
                <div style={{ 
                  backgroundColor: isExpired ? '#fff6f6' : '#e8f5e8', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: isExpired ? '2px solid #db2828' : '2px solid #21ba45'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <Icon 
                      name={isExpired ? "exclamation triangle" : "shield check"} 
                      color={isExpired ? "red" : "green"} 
                      size="large" 
                    />
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: isExpired ? '#db2828' : '#21ba45',
                      marginLeft: '10px'
                    }}>
                      {isExpired ? 'EXPIRED DOCUMENT' : 'VERIFIED DOCUMENT'}
                    </span>
                  </div>
                  <p style={{ 
                    margin: '0', 
                    color: isExpired ? '#9f3a38' : '#2d5016', 
                    fontSize: '14px' 
                  }}>
                    {isExpired 
                      ? 'This offer letter has been verified as authentic, but the acceptance deadline has expired.'
                      : 'This offer letter has been successfully verified and is authentic.'
                    }
                  </p>
                </div>
                
                <Table celled striped style={{ marginTop: '20px' }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell style={{ backgroundColor: '#f8f9fa', width: '30%' }}>Field</Table.HeaderCell>
                      <Table.HeaderCell style={{ backgroundColor: '#f8f9fa' }}>Details</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell><strong>Offer Letter ID</strong></Table.Cell>
                      <Table.Cell style={{ fontFamily: 'monospace', color: '#0066cc' }}>
                        {offerLetterData.offer_letter_id}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Candidate Name</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name="user" color="blue" />
                        {offerLetterData.name}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Company</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name="building" color="teal" />
                        {offerLetterData.your_company_name}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Position/Title</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name="briefcase" color="orange" />
                        {offerLetterData.your_title}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Issuer Name</strong></Table.Cell>
                      <Table.Cell>{offerLetterData.your_full_name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Designation</strong></Table.Cell>
                      <Table.Cell>{offerLetterData.designation}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Working Mode</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name={offerLetterData.working_mode?.toLowerCase() === 'wfo' ? 'building' : 'home'} />
                        {offerLetterData.working_mode} 
                        {offerLetterData.working_mode?.toLowerCase() === 'wfo' ? ' (Work From Office)' : ''}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Date of Joining</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name="calendar" color="blue" />
                        {formatDate(offerLetterData.date_of_joining)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row style={{ 
                      backgroundColor: isExpired ? '#ffebee' : (daysDiff <= 3 && daysDiff > 0 ? '#fff3e0' : 'inherit')
                    }}>
                      <Table.Cell><strong>Acceptance Deadline</strong></Table.Cell>
                      <Table.Cell>
                        <Icon 
                          name={isExpired ? "times circle" : (daysDiff <= 3 && daysDiff > 0 ? "exclamation circle" : "clock")} 
                          color={isExpired ? "red" : (daysDiff <= 3 && daysDiff > 0 ? "orange" : "blue")} 
                        />
                        {formatDate(offerLetterData.acceptance_deadline)}
                        {isExpired && (
                          <span style={{ color: '#db2828', fontWeight: 'bold', marginLeft: '10px' }}>
                            (EXPIRED)
                          </span>
                        )}
                        {!isExpired && daysDiff <= 3 && daysDiff > 0 && (
                          <span style={{ color: '#f2711c', fontWeight: 'bold', marginLeft: '10px' }}>
                            ({daysDiff} {daysDiff === 1 ? 'day' : 'days'} remaining)
                          </span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Annual Salary</strong></Table.Cell>
                      <Table.Cell style={{ fontSize: '16px', fontWeight: 'bold', color: '#21ba45' }}>
                        <Icon name="rupee sign" />
                        {formatSalary(offerLetterData.salary)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Document Created</strong></Table.Cell>
                      <Table.Cell>
                        <Icon name="file" />
                        {formatDate(offerLetterData.created_at)}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}
          </Card>
        </Segment>
      </Modal.Content>

      <Modal.Actions style={{ 
        backgroundColor: '#f8f9fa', 
        borderTop: '1px solid #e9ecef',
        padding: '15px 30px'
      }}>
        <Button 
          onClick={() => setIsOfferLetterVerifyModalOpen(false)}
          size="large"
        >
          <Icon name="close" />
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default OfferLetterVerifyModal;